import type { FC, ReactNode } from 'react'
import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableContainer from '@material-ui/core/TableContainer'
import TableRow from '@material-ui/core/TableRow'
import type { ITheme } from '@homeviews/styles'
import type { ISort } from '@homeviews/models'
import { isEqual } from '@homeviews/utils'
import Pagination from '../Pagination'
import type {
  HeadCells,
  OnSortChange,
  RowOfCells,
  Rows,
  SortType,
} from './table.types'
import EnhancedTableHead from './EnhancedTableHead'

const useStyles = makeStyles((theme: ITheme) =>
  createStyles({
    root: {
      width: '100%',
      '& .MuiTableCell-head': {
        fontSize: '12px',
        fontWeight: 500,
        letterSpacing: '0.2px',
        lineHeight: '22px',
        color: theme.colors.grey600,
        padding: theme.spacing(0.75, 2),
        backgroundColor: theme.colors.grey200,
        borderRight: `1px solid ${theme.colors.grey250}`,
        borderColor: `${theme.colors.grey250}!important`,
        position: 'relative',
        paddingRight: 20,
        '&:last-child': {
          borderRight: 'none',
        },
        '&:hover': {
          '& .l-sortArrowWrapper': {
            opacity: 1,
            transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
          },
        },
      },
      '& .MuiTableRow-root': {
        backgroundColor: theme.colors.white,
      },
      '& .MuiTableCell-body': {
        color: theme.colors.grey480,
        fontSize: 16,
        letterSpacing: 0,
        lineHeight: '22px',
        fontWeight: 400,
        padding: theme.spacing(0.9375, 2),
        borderColor: `${theme.colors.grey250}!important`,
        wordBreak: 'break-word',
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '& .sort_asc': {
        opacity: 1,
        transform: 'rotate(-180deg)',
        transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      },
      '& .sort_desc': {
        opacity: 1,
        transform: 'rotate(0deg)',
        transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      },
      '& .l-table__actionsCell': {
        display: 'flex',
      },
      '& .l-table__link': {
        '&:hover': {
          cursor: 'pointer',
        },
      },
      '& .l-table__link--delete': {
        color: theme.colors.red,
      },
      '& .l-table__link--primary': {
        color: theme.colors.primaryLink,
        marginRight: 20,
      },
      '& .l-table__cell--award': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        '& img': {
          marginRight: 5,
          marginTop: 1,
        },
      },
      '& .l-table__cell--awardCount': {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
      '& .l-table__imageWrapper': {
        position: 'relative',
        width: 85,
        height: 56,
        '& img': {
          objectFit: 'cover',
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        },
      },
      '& .l-table__iconWrapper': {
        width: 56,
        height: 40,
        '& img': {
          objectFit: 'contain',
        },
      },
    },
    noClickableRow: {
      '& .MuiTableCell-body': {
        '&:hover': {
          cursor: 'initial',
        },
      },
    },
    fontStyleSmall: {
      '& .MuiTableCell-body': {
        color: theme.colors.grey800,
        fontSize: 14,
      },
    },
    benchmarkTable: {
      '& .MuiTable-root': {
        borderTop: 'none',
        borderRightColor: 'transparent',
        borderLeftColor: 'transparent',
      },
      '& .MuiTableRow-head': {},
      '& th': {
        border: 'none!important',
        backgroundColor: `${theme.colors.primary}!important`,
        color: `#000433 !important`,
        fontSize: '14px!important',
        padding: '12px 15px!important',
        textAlign: 'left!important',
        '&:first-child': {
          opacity: 0,
        },
        '&:last-child': {
          borderTopRightRadius: 5,
        },
        '&:nth-child(2)': {
          borderTopLeftRadius: 5,
        },
      },
      '&:not(.c-table--wasPaginated)': {
        '& tbody': {
          '& tr': {
            '&:first-child': {
              '& td': {
                color: `#F5A512 !important`,
                borderTopLeftRadius: 5,
                backgroundColor: 'rgba(187,161,90,0.05)!important',
                borderBottom: '1px solid  rgba(187,161,90,0.3)!important',

                '&:first-child': {
                  backgroundColor: `#F5A512 !important`,
                  color: `#000433 !important`,
                  fontSize: '14px!important',
                  padding: '12px 15px!important',
                },
              },
            },
          },
        },
      },
      '& tbody': {
        '& tr': {
          '& td': {
            padding: '12px 15px!important',
            '&:first-child': {
              borderLeft: `1px solid ${theme.colors.grey250}!important`,
            },
            '&:last-child': {
              borderRight: `1px solid ${theme.colors.grey250}!important`,
            },
          },
        },
      },
    },
    paper: {
      width: '100%',
      marginBottom: theme.spacing(2),
    },
    table: {
      minWidth: 750,
      boxShadow: '1px 3px 6px 0 rgba(0,0,0,0.05)',
      border: `1px solid ${theme.colors.grey250}`,
    },
    sortArrowWrapper: {
      opacity: 0,
      transition: 'transform .3s ease-in-out, opacity .3s ease-in-out',
      position: 'absolute',
      top: 8,
      right: 10,
      height: 18,
      width: 18,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '&:hover': {
        cursor: 'pointer',
      },
    },
    paginationWrapper: {
      marginTop: 25,
    },
  }),
)

interface ITableProps {
  headCells: HeadCells
  rows: Rows
  stickyRows?: Rows
  onRowClick?: (id: number) => void
  onSortChange?: OnSortChange
  activeSort?: ISort
  fontStyle?: 'default' | 'small'
  layout?: 'benchmarkTable'
  paginated?: boolean
  sortable?: boolean
  defaultSort?: SortType
}

interface IState {
  page: number
  perPage: number
  lastPage: number
  activeSort?: SortType
  rows: Rows
  filteredRows: Rows
}

const EnhancedTable: FC<ITableProps> = ({
  headCells,
  rows,
  stickyRows,
  onRowClick,
  onSortChange,
  activeSort,
  fontStyle,
  layout,
  paginated,
  sortable,
  defaultSort,
}) => {
  const classes = useStyles()

  const [state, setState] = useState<IState>({
    rows,
    filteredRows: [],
    page: 1,
    perPage: 10,
    lastPage: 1,
    activeSort: defaultSort || null,
  })

  useEffect(() => {
    let paginatedRows: Rows = rows?.length ? [...rows] : []

    if (sortable) {
      paginatedRows = sortData(paginatedRows)
    }

    if (paginated) {
      paginatedRows = paginate(paginatedRows, state.perPage, state.page)
    }

    setState({
      ...state,
      filteredRows: paginatedRows,
      rows,
      page: isEqual(rows, state.rows) ? state.page : 1,
      lastPage: Math.ceil(rows.length / state.perPage),
    })
  }, [rows, state.page, state.perPage])

  const handleRowClick = (id: number) => {
    typeof onRowClick === 'function' && onRowClick(id)
  }

  const handlePerPageChange = (perPage: number) => {
    setState({
      ...state,
      page: 1,
      perPage,
    })
  }

  const handlePageChange = (page: number) => {
    setState({
      ...state,
      page,
    })
  }

  const sortData = (sortableRows: Rows): Rows => {
    const prevRows: Rows = [...sortableRows]
    const newOrder = state.activeSort?.order || 'desc'
    const sortIndex = state.activeSort?.orderByIndex

    if (!sortIndex) {
      return prevRows
    }
    const newRows: Rows = prevRows.sort((a: RowOfCells, b: RowOfCells) => {
      if (newOrder === 'desc') {
        return (b?.[sortIndex] as string)?.localeCompare?.(
          a?.[sortIndex] as string,
          undefined,
          {
            numeric: true,
            sensitivity: 'base',
          },
        )
      }
      return (a?.[sortIndex] as string)?.localeCompare?.(
        b?.[sortIndex] as string,
        undefined,
        {
          numeric: true,
          sensitivity: 'base',
        },
      )
    })
    return newRows
  }

  return (
    <div
      className={clsx(
        classes.root,
        !onRowClick && classes.noClickableRow,
        layout === 'benchmarkTable' && classes.benchmarkTable,
        fontStyle === 'small' && classes.fontStyleSmall,
      )}
    >
      <TableContainer>
        <Table
          className={classes.table}
          aria-labelledby="tableTitle"
          size="small"
          aria-label="enhanced table"
          style={{
            tableLayout: 'fixed',
          }}
        >
          <EnhancedTableHead
            classes={classes}
            cells={headCells}
            onSortChange={onSortChange}
            activeSort={activeSort}
          />
          <TableBody>
            {!!stickyRows?.length &&
              stickyRows.map((row, rowIndex: number) => {
                return (
                  <TableRow key={`sticky--${rowIndex}`}>
                    {!!row?.length &&
                      row.map((cell: ReactNode, index: number) => {
                        return (
                          index > 0 && (
                            <TableCell align="left" key={index}>
                              {cell}
                            </TableCell>
                          )
                        )
                      })}
                  </TableRow>
                )
              })}
            {state.filteredRows.map((row, rowIndex: number) => {
              return (
                <TableRow
                  key={rowIndex}
                  onClick={() => handleRowClick(row[0] as number)}
                >
                  {!!row?.length &&
                    row.map((cell: ReactNode, index: number) => {
                      return (
                        index > 0 && (
                          <TableCell align="left" key={index}>
                            {cell}
                          </TableCell>
                        )
                      )
                    })}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {paginated && (
        <div className={classes.paginationWrapper}>
          <Pagination
            currentPage={state.page}
            lastPage={state.lastPage}
            perPage={state.perPage}
            onPageChange={handlePageChange}
            onPerPageChange={handlePerPageChange}
          />
        </div>
      )}
    </div>
  )
}

export default EnhancedTable

function paginate(array: Rows, page_size: number, page_number: number) {
  return array.slice((page_number - 1) * page_size, page_number * page_size)
}
